import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = "";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      // Fetch organization if user has one
      if (response.data.org_id) {
        try {
          const orgResponse = await axios.get(`${API_URL}/api/organizations/current`, {
            withCredentials: true
          });
          setOrganization(orgResponse.data);
        } catch (e) {
          console.log('No organization found');
        }
      }
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    if (response.data.org_id) {
      try {
        const orgResponse = await axios.get(`${API_URL}/api/organizations/current`, {
          withCredentials: true
        });
        setOrganization(orgResponse.data);
      } catch (e) {
        console.log('No organization found');
      }
    }
    return response.data;
  };

  const register = async (email, password, name, orgName) => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name, org_name: orgName },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setOrganization(null);
  };

  const updateOrganization = async (data) => {
    const response = await axios.put(`${API_URL}/api/organizations/current`, data, {
      withCredentials: true
    });
    setOrganization(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      organization,
      login,
      register,
      logout,
      checkAuth,
      updateOrganization,
      isAdmin: user?.role === 'admin',
      isProcurement: user?.role === 'procurement',
      isStores: user?.role === 'stores',
    }}>
      {children}
    </AuthContext.Provider>
  );
};
